import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { RefObject } from 'react';
import { blogData } from '../../../mocks/blog';

const toSlug = (title: string) =>
  title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

export default function Blog() {
  const navigate = useNavigate();
  const featuredArticles = blogData.slice(0, 3);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section id="blog" className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef as RefObject<HTMLDivElement>}
          className={`text-center mb-6 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-3 py-1 bg-slate-100 border border-slate-900/10 text-slate-900/70 text-sm font-semibold uppercase tracking-widest rounded">
            Latest Insights
          </span>
        </div>
        <h2
          className={`text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 text-center leading-tight mb-10 transition-all duration-700 delay-100 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          News &amp; Articles
        </h2>

        {/* Articles Grid - Optimized with lazy loading */}
        <div
          ref={gridRef as RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-10"
        >
          {featuredArticles.map((article, index) => (
            <article
              key={index}
              onClick={() => navigate(`/resources/${toSlug(article.title)}`)}
              style={{ transitionDelay: `${index * 120}ms` }}
              className={`group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer border border-slate-100 ${
                gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <div className="relative h-56 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  width={600}
                  height={400}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="inline-block px-3 py-1 bg-white text-slate-900 text-xs font-semibold rounded-full">
                    {article.category}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-amber-500 transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-3">
                  {article.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center text-white text-xs font-semibold">
                      {article.author.charAt(0)}
                    </div>
                    <span className="text-sm text-slate-700 font-medium">{article.author}</span>
                  </div>
                  <span className="text-amber-500 font-semibold flex items-center gap-2 group-hover:gap-3 transition-all whitespace-nowrap">
                    Read More
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* View All Link */}
        <div className={`text-center transition-all duration-700 delay-300 ${gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <button
            onClick={() => navigate('/resources')}
            className="text-amber-500 text-lg font-semibold underline underline-offset-4 hover:text-amber-600 transition-colors cursor-pointer"
          >
            View All Articles
          </button>
        </div>
      </div>
    </section>
  );
}
